import {
  AspectRatio,
  Box,
  Flex,
  Icon,
  ListItem,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  VStack,
  Divider,
} from '@chakra-ui/react'
import React from 'react'
import { Base64 } from 'js-base64'
import { isNull } from 'lodash'
import moment from 'moment'
import Highlighter from 'react-highlight-words'
import { AiOutlineHistory } from 'react-icons/ai'
import { IoMdAdd } from 'react-icons/io'
import { IoSearch } from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import AWSIcon from '../../../../../graphics/AWSIcon'
import GoogleIcon from '../../../../../graphics/GoogleIcon'
import { CollectionIcon } from '../../../../../graphics/media-library/CollectionIcon'
import { FolderIcon } from '../../../../../graphics/media-library/FolderIcon'
import { TagGraphic } from '../../../../../graphics/media-library/TagIcon'
import { colors } from '../../../../../ui-kit'
import { mediaLibraryThumbnailTransformation } from '../../../../../utils/mediaLibrary'
import FilePreview from '../../FilePreview'
import {
  findChunksAtBeginningOfWords,
} from '../../utils'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  TagPropsInterface,
} from '../../../../interfaces'

// define wrapper for the highlightedText component
export const HighlightedTextComponentWrapper = styled.div`
  display: flex;
  max-width: 100%;
  .highlighted-text-component {
    display: inline-block;
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
  }
`

/** simple labeled row header */
export const SectionHeader = ({
  children,
  noPx = false,
}: {
  children: React.ReactNode
  noPx?: boolean
}) => (
  <Box
    color="#3b485e"
    fontSize="14px"
    fontWeight="500"
    lineHeight="150%"
    px={noPx ? '0' : '16px'}
    mt="10px"
    mb="8px"
  >
    {children}
  </Box>
)

export const Row = ({
  active,
  children,
  onClick,
  itemProps,
  ...props
}: {
  active?: boolean
  children: React.ReactNode
  onClick?(event: React.MouseEvent): void
  itemProps?: any // For downshift getItemProps
} & React.HTMLAttributes<HTMLElement>) => (
  <ListItem
    display="flex"
    justifyContent="space-between"
    width="full"
    backgroundColor={active ? '#F3F8FF' : 'appWhite'}
    paddingY="0.5rem"
    paddingX="1rem"
    cursor="pointer"
    _hover={{ backgroundColor: '#F3F8FF' }}
    onClick={onClick}
    {...itemProps}
    {...props}
  >
    {children}
  </ListItem>
)

export const Highlighted = ({
  text,
  query,
  enabled = true,
}: {
  text: string
  query: string
  enabled?: boolean
}) => {
  if (!enabled) {
    return <>{text}</>
  }
  return (
    <Highlighter
      className="highlighted-text-component"
      searchWords={[query]}
      textToHighlight={text}
      findChunks={findChunksAtBeginningOfWords}
      highlightStyle={{
        backgroundColor: '#CDDCF7',
        fontWeight: 500,
        fontFamily: 'Poppins',
        lineHeight: '150%',
      }}
    />
  )
}

export const TagComponent = ({
  tag,
  searchTerm,
  maxWidth,
  icon = TagGraphic,
  enableHighlighting = true,
}: TagPropsInterface & { enableHighlighting?: boolean }): JSX.Element => (
  <Flex
    alignItems="center"
    justifyContent="start"
    padding="1px 6px 1px 4px"
    gap="2px"
    borderRadius="4px"
    border="1px solid #E6E7EA"
    background="#FFF"
    maxWidth={maxWidth ?? 'full'}
  >
    <Icon
      as={icon}
      w="16px"
      h="16px"
      p={icon === GoogleIcon ? '1.5px' : '0'}
      mt={icon === AWSIcon ? '5px' : '0'}
      mr={icon === AWSIcon ? '1.5px' : '0'}
      color={colors.appBattleshipGrey}
    />
    {searchTerm && enableHighlighting ? (
      <Flex overflow="hidden">
        <HighlightedTextComponentWrapper>
          <Highlighter
            className="highlighted-text-component"
            searchWords={[searchTerm]}
            textToHighlight={tag}
            findChunks={findChunksAtBeginningOfWords}
            highlightStyle={{
              backgroundColor: '#CDDCF7',
              fontWeight: '500',
              fontStyle: 'normal',
              fontFamily: 'Poppins',
              lineHeight: '150%',
              fontSize: '12px',
            }}
            unhighlightStyle={{
              color: '#1A202C',
              fontWeight: '400',
              fontStyle: 'normal',
              fontFamily: 'Poppins',
              lineHeight: '150%',
              fontSize: '12px',
            }}
          />
        </HighlightedTextComponentWrapper>
      </Flex>
    ) : (
      <Text
        color="#1A202C"
        fontFamily="Poppins"
        fontSize="12px"
        fontStyle="normal"
        fontWeight="400"
        lineHeight="150%"
        isTruncated
      >
        {tag}
      </Text>
    )}
  </Flex>
)

// Helper function to format folder path for display
export const formatFolderPath = (folderPath: string): string => {
  const pathParts = folderPath.split('/').filter(Boolean).slice(0, -1) // Remove the folder itself, show parent path
  if (pathParts.length === 0) {
    return 'Home'
  }
  if (pathParts.length <= 2) {
    return `/${pathParts.join('/')}`
  }
  return `/${pathParts[0]}/.../${pathParts[pathParts.length - 1]}`
}

// Helper function to get parent path from full path
export const getParentPath = (fullPath: string): string => {
  const pathParts = fullPath.split('/').filter(Boolean).slice(0, -1)
  return `/${pathParts.join('/')}`
}

// Render folder item in list
export const RenderFolderItem: React.FC<{
  folder: MediaLibrarySearchFolderResponse
  index: number
  highlightedIndex: number
  debouncedQ: string
  itemProps: any
  searchMode: string | null
}> = ({ folder, index, highlightedIndex, debouncedQ, itemProps, searchMode }) => {
  const history = useHistory()
  const isHighlighted = highlightedIndex === index
  const enableHighlighting = searchMode !== 'ai-image' && searchMode !== 'ai-text'

  return (
    <Row
      key={`folder-${folder.folderId}`}
      active={isHighlighted}
      itemProps={itemProps}
    >
      <Flex w="100%" gap="8px" align="center" minW="0" overflow="hidden">
        <Icon
          as={FolderIcon}
          w="24px"
          h="24px"
          color={colors.appBattleshipGrey}
          flexShrink={0}
        />

        {isHighlighted ? (
          <Flex
            w="100%"
            gap="8px"
            alignItems="center"
            minW="0"
            overflow="hidden"
          >
            <Text minW="0" whiteSpace="nowrap" isTruncated maxW="40%">
              <Highlighted text={folder.name} query={debouncedQ} enabled={enableHighlighting} />
            </Text>

            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              •
            </Text>

            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {`${moment(folder.createdAt).local().format('DD MMM YYYY')}`}
            </Text>

            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              •
            </Text>

            <Box
              flex="1"
              minW="0"
              overflow="hidden"
              display="flex"
              alignItems="center"
            >
              <Text
                color="appCoolGrey"
                fontFamily="Poppins"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="150%"
                cursor="pointer"
                _hover={{
                  color: 'appBlue',
                  textDecoration: 'underline',
                }}
                whiteSpace="nowrap"
                isTruncated
                maxW="100%"
                onClick={e => {
                  e.stopPropagation()
                  const parentPath = getParentPath(folder.folderPath)
                  history.push(
                    `/media-library/${Base64.encode(parentPath, true)}`,
                  )
                  window.dispatchEvent(
                    new CustomEvent('mlSearchAutocomplete:close'),
                  )
                }}
              >
                {formatFolderPath(folder.folderPath)}
              </Text>
            </Box>
          </Flex>
        ) : (
          <Flex w="100%" minW="0" overflow="hidden">
            <Text whiteSpace="nowrap" isTruncated>
              <Highlighted text={folder.name} query={debouncedQ} enabled={enableHighlighting} />
            </Text>
          </Flex>
        )}
      </Flex>
    </Row>
  )
}

// Render file item in list
export const RenderFileItem: React.FC<{
  file: MediaLibrarySearchFileResponse
  index: number
  highlightedIndex: number
  debouncedQ: string
  itemProps: any
  searchByTags: boolean
  searchMode: string | null
}> = ({
  file,
  index,
  highlightedIndex,
  debouncedQ,
  itemProps,
  searchByTags,
  searchMode,
}) => {
    const history = useHistory()
    const isHighlighted = highlightedIndex === index
    const enableHighlighting = searchMode !== 'ai-image' && searchMode !== 'ai-text'

    return (
      <Row
        key={`file-${file.fileId}-${file.versionInfo.id ?? 'v0'}`}
        active={isHighlighted}
        itemProps={itemProps}
      >
        <Flex w="100%" gap="8px" align="center" minW="0" overflow="hidden">
          <AspectRatio w="24px" h="24px" flexShrink={0}>
            <FilePreview
              file={file}
              folderPath={file.filePath}
              transformation={mediaLibraryThumbnailTransformation}
              disablePadding={true}
            />
          </AspectRatio>

          {isHighlighted ? (
            <Flex
              w="100%"
              gap="8px"
              alignItems="center"
              minW="0"
              overflow="hidden"
            >
              <Text minW="0" whiteSpace="nowrap" isTruncated maxW="40%">
                <Highlighted text={file.name} query={debouncedQ} enabled={enableHighlighting} />
              </Text>

              {searchByTags &&
                (() => {
                  let filteredTags: Array<{
                    tagLabel: string
                    info?: any
                  }> =
                    !isNull(file.tags) || !isNull(file.AITags)
                      ? [
                        ...(file.tags
                          ? file.tags.map(tag => ({ tagLabel: tag }))
                          : []),
                        ...(file.AITags
                          ? file.AITags.map(AITag => ({
                            tagLabel: AITag.name,
                            info: AITag,
                          }))
                          : []),
                      ].filter(
                        tag =>
                          findChunksAtBeginningOfWords({
                            searchWords: [debouncedQ],
                            textToHighlight: tag.tagLabel,
                          }).length > 0,
                      )
                      : []

                  let remainingFilteredTagsLength = 0
                  if (filteredTags.length > 1) {
                    remainingFilteredTagsLength = filteredTags.length - 1
                    filteredTags = filteredTags.slice(0, 1)
                  }

                  return (
                    <Flex gap="8px" overflow="hidden" flexShrink={0}>
                      {filteredTags.map((tag, tagIndex) => (
                        <TagComponent
                          key={tagIndex}
                          tag={tag.tagLabel}
                          searchTerm={debouncedQ}
                          maxWidth={
                            remainingFilteredTagsLength ||
                              tag.tagLabel.length > 15
                              ? '150px'
                              : 'full'
                          }
                          icon={
                            tag.info?.source === 'google-auto-tagging'
                              ? GoogleIcon
                              : tag.info?.source === 'aws-auto-tagging'
                                ? AWSIcon
                                : TagGraphic
                          }
                          enableHighlighting={enableHighlighting}
                        />
                      ))}
                      {remainingFilteredTagsLength > 0 && (
                        <Flex maxW="80px">
                          <Tag
                            color={colors.appBattleshipGrey}
                            padding="1px 6px 1px 4px"
                          >
                            <TagLeftIcon boxSize="10px" as={IoMdAdd} mr={0} />
                            <TagLabel fontSize="12px">
                              {`${remainingFilteredTagsLength} tags`}
                            </TagLabel>
                          </Tag>
                        </Flex>
                      )}
                    </Flex>
                  )
                })()}

              <Text
                color="appCoolGrey"
                fontFamily="Poppins"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="150%"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                •
              </Text>

              <Text
                color="appCoolGrey"
                fontFamily="Poppins"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="150%"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {`${moment(file.createdAt).local().format('DD MMM YYYY')}`}
              </Text>

              <Text
                color="appCoolGrey"
                fontFamily="Poppins"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="150%"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                •
              </Text>

              <Box
                flex="1"
                minW="0"
                overflow="hidden"
                display="flex"
                alignItems="center"
              >
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="12px"
                  fontStyle="normal"
                  fontWeight="400"
                  lineHeight="150%"
                  cursor="pointer"
                  _hover={{
                    color: 'appBlue',
                    textDecoration: 'underline',
                  }}
                  whiteSpace="nowrap"
                  isTruncated
                  maxW="100%"
                  onClick={e => {
                    e.stopPropagation()
                    const parentPath = getParentPath(file.filePath)
                    history.push(
                      `/media-library/${Base64.encode(parentPath, true)}`,
                    )
                    window.dispatchEvent(
                      new CustomEvent('mlSearchAutocomplete:close'),
                    )
                  }}
                >
                  {formatFolderPath(file.filePath)}
                </Text>
              </Box>
            </Flex>
          ) : (
            <Flex
              w="100%"
              gap="8px"
              alignItems="center"
              minW="0"
              overflow="hidden"
            >
              <Text whiteSpace="nowrap" isTruncated>
                <Highlighted text={file.name} query={debouncedQ} enabled={enableHighlighting} />
              </Text>

              {searchByTags &&
                (() => {
                  let filteredTags: Array<{
                    tagLabel: string
                    info?: any
                  }> =
                    !isNull(file.tags) || !isNull(file.AITags)
                      ? [
                        ...(file.tags
                          ? file.tags.map(tag => ({ tagLabel: tag }))
                          : []),
                        ...(file.AITags
                          ? file.AITags.map(AITag => ({
                            tagLabel: AITag.name,
                            info: AITag,
                          }))
                          : []),
                      ].filter(
                        tag =>
                          findChunksAtBeginningOfWords({
                            searchWords: [debouncedQ],
                            textToHighlight: tag.tagLabel,
                          }).length > 0,
                      )
                      : []

                  let remainingFilteredTagsLength = 0
                  if (filteredTags.length > 1) {
                    remainingFilteredTagsLength = filteredTags.length - 1
                    filteredTags = filteredTags.slice(0, 1)
                  }

                  return (
                    <Flex gap="8px" overflow="hidden" flexShrink={0}>
                      {filteredTags.map((tag, tagIndex) => (
                        <TagComponent
                          key={tagIndex}
                          tag={tag.tagLabel}
                          searchTerm={debouncedQ}
                          maxWidth={
                            remainingFilteredTagsLength ||
                              tag.tagLabel.length > 15
                              ? '150px'
                              : 'full'
                          }
                          icon={
                            tag.info?.source === 'google-auto-tagging'
                              ? GoogleIcon
                              : tag.info?.source === 'aws-auto-tagging'
                                ? AWSIcon
                                : TagGraphic
                          }
                          enableHighlighting={enableHighlighting}
                        />
                      ))}
                      {remainingFilteredTagsLength > 0 && (
                        <Flex maxW="80px">
                          <Tag
                            color={colors.appBattleshipGrey}
                            padding="1px 6px 1px 4px"
                          >
                            <TagLeftIcon boxSize="10px" as={IoMdAdd} mr={0} />
                            <TagLabel fontSize="12px">
                              {`${remainingFilteredTagsLength} tags`}
                            </TagLabel>
                          </Tag>
                        </Flex>
                      )}
                    </Flex>
                  )
                })()}
            </Flex>
          )}
        </Flex>
      </Row>
    )
  }

// Render collection item in list
export const RenderCollectionItem = ({
  collection,
  index,
  highlightedIndex,
  debouncedQ,
  itemProps,
  searchMode,
}: {
  collection: MediaLibrarySearchCollectionResponse
  index: number
  highlightedIndex: number
  debouncedQ: string
  itemProps: any
  searchMode: string | null
}) => {
  const isHighlighted = highlightedIndex === index
  const enableHighlighting = searchMode !== 'ai-image' && searchMode !== 'ai-text'

  return (
    <Row
      key={`collection-${collection.mediaCollectionId}`}
      active={isHighlighted}
      itemProps={itemProps}
    >
      <Flex w="100%" gap="8px" align="center" minW="0" overflow="hidden">
        {collection.collectionThumbnail ? (
          <AspectRatio w="24px" h="24px" flexShrink={0}>
            <Box
              backgroundImage={`url(${collection.collectionThumbnail})`}
              backgroundPosition="center"
              backgroundSize="cover"
              backgroundRepeat="no-repeat"
              borderRadius="2px"
            />
          </AspectRatio>
        ) : (
          <Icon
            as={CollectionIcon}
            h="24px"
            w="24px"
            color={colors.appBattleshipGrey}
            flexShrink={0}
          />
        )}

        {isHighlighted ? (
          <Flex
            w="100%"
            gap="8px"
            alignItems="center"
            minW="0"
            overflow="hidden"
          >
            <Text minW="0" whiteSpace="nowrap" isTruncated maxW="40%">
              <Highlighted text={collection.name} query={debouncedQ} enabled={enableHighlighting} />
            </Text>

            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              •
            </Text>

            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="150%"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {`${moment(collection.createdAt).local().format('DD MMM YYYY')}`}
            </Text>

            {typeof collection.size === 'number' && (
              <>
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="12px"
                  fontStyle="normal"
                  fontWeight="400"
                  lineHeight="150%"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  •
                </Text>

                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="12px"
                  fontStyle="normal"
                  fontWeight="400"
                  lineHeight="150%"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {`${collection.size} item${collection.size === 1 ? '' : 's'}`}
                </Text>
              </>
            )}
          </Flex>
        ) : (
          <Flex w="100%" minW="0" overflow="hidden">
            <Text whiteSpace="nowrap" isTruncated>
              <Highlighted text={collection.name} query={debouncedQ} enabled={enableHighlighting} />
            </Text>
          </Flex>
        )}
      </Flex>
    </Row>
  )
}

// Render recent searches section
export const RenderRecentSearches = ({
  recentSearches,
  pushToResults,
}: {
  recentSearches: Record<string, any>
  pushToResults(params: any): void
}) => (
  <>
    <Divider />
    <Box width="full">
      <SectionHeader>Recent Searches</SectionHeader>
      {recentSearches && Object.keys(recentSearches).length > 0 ? (
        <VStack overflow="auto" maxH="50vh">
          {Object.keys(recentSearches || {}).map((term, _index) => (
            <Box
              key={`recent-${term}`}
              display="flex"
              justifyContent="space-between"
              width="full"
              paddingY="0.5rem"
              paddingX="1rem"
              cursor="pointer"
              _hover={{ backgroundColor: '#F3F8FF' }}
              onClick={() => {
                pushToResults({
                  searchMode: 'keyword',
                  q: term,
                  searchQuery: '',
                  searchByTags: false,
                  isVersionIncludedInSearch: false,
                  fileType: { text: '', value: '' },
                  searchInputMode: 'builder',
                })
              }}
            >
              <Flex w="100%" gap="8px" alignItems="center">
                <Icon as={AiOutlineHistory} color="#718096" boxSize="1.1rem" />
                <Text color="#3b485e" isTruncated>
                  {term}
                </Text>
              </Flex>
            </Box>
          ))}
        </VStack>
      ) : (
        <Flex w="full" px="16px">
          <Text color="appBattleshipGrey">No recent searches</Text>
        </Flex>
      )}
    </Box>
  </>
)

// Render no results message
export const RenderNoResults = () => (
  <Flex w="full" px="16px" py="0.5rem">
    <Text color="appBattleshipGrey">No results found</Text>
  </Flex>
)

// Render hardcoded option (Search all results, Search for images related)
export const RenderHardcodedOption: React.FC<{
  item: { kind: 'option', label: string, action(): void }
  index: number
  highlightedIndex: number
  itemProps: any
}> = ({ item, index, highlightedIndex, itemProps }) => (
  <Row active={highlightedIndex === index} itemProps={itemProps}>
    <Flex w="100%" gap="8px" align="center" minW={0}>
      <Icon as={IoSearch} color={colors.appBattleshipGrey} boxSize="1.2rem" />
      <Text isTruncated>{item.label}</Text>
    </Flex>
  </Row>
)

